import React from 'react';
import i18next from 'i18next';
import type { IInitSettings } from '../init';
import { useSelector } from 'react-redux';
import { eplassSliceDocumentUrl, eplassSliceViewMode } from '../Redux/EplassSlice';
import WebViewer, { type WebViewerInstance } from '@pdftron/webviewer';

import styles from './PdfViewer.module.scss';
import { GlobalDialogs } from '../pdf/GlobalDialogs';
import { ReduxUpdater } from '../pdf/ReduxUpdater';
import { checkExtension, devLog, signatureArrayLength, upperCaseBegin } from '../utils';
import { PdfViewerViewModeFlag } from '../pdf/Enums';
import { ApryseUpdater } from '../pdf/ApryseUpdater';
import { ApryseSDK } from '../pdf/ApryseSDK';
import { backendApiUrl, onWindowKeyDown } from '../App';
import { GlobalConstants } from '../pdf';
import { GlobalVariables } from '../pdf/GlobalVariables';
import { ToastSeverity } from './General/Toast/Toast';
import PasswordDialog from './Layout/Dialogs/PasswordDialog';
import RulerOverlay from './Layout/Ruler/RulerOverlay';
import { useRuler } from '../RulerContext';

/**
 * Properties for the PdfViewer component.
 */
export type IPdfViewerProps = Pick<
    IInitSettings & {
        className: string;
        customFontURL?: string;
        isDark?: boolean;
        onPdfOpened: () => Promise<void>;
        onPdfError: (error: Error) => void;
    },
    'className' | 'customFontURL' | 'isDark' | 'lic' | 'onPdfOpened' | 'onPdfError' | 'testRun'
>;

export default (props: IPdfViewerProps) => {
    const { className, lic, onPdfOpened, onPdfError, customFontURL, isDark } = props;
    const documentUrl = useSelector(eplassSliceDocumentUrl);
    const viewMode = useSelector(eplassSliceViewMode);
    const { showRuler } = useRuler();

    const elRef = React.useRef<HTMLDivElement>(null);
    const isDarkRef = React.useRef<boolean>(isDark ?? false);
    const [viewerReady, setViewerReady] = React.useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
    const [passwordError, setPasswordError] = React.useState('');
    const blobUrlRef = React.useRef<string | null>(null);
    const passwordExtensionRef = React.useRef<string>('');

    const handlePasswordConfirm = async (password: string) => {
        const blobUrl = blobUrlRef.current;
        const instance = ApryseSDK.instance;
        if (!instance || !blobUrl) return;
        try {
            await instance.Core.documentViewer.loadDocument(blobUrl, {
                extension: passwordExtensionRef.current,
                password,
            });
            setPasswordDialogOpen(false);
        } catch {
            setPasswordError(i18next.t('passwordDialog.wrongPassword'));
        }
    };

    const handlePasswordCancel = () => {
        setPasswordDialogOpen(false);
    };

    const handleError = (err: Error) =>
        GlobalDialogs.showNotyDebounced(2.5, err.message, ToastSeverity.Error);

    const onUnhandledRejection = (e: PromiseRejectionEvent): void => {
        handleError(e.reason || new Error(i18next.t('errors.init')));
    };

    const blockContextMenu = (e: Event): void => {
        e.preventDefault();
    };

    // Handle errors during initialization
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    document.addEventListener('contextmenu', blockContextMenu);

    const processDigitalSignatures = async () => {
        if (process.env.REACT_APP_SIGNATURE_CERTIFICATES) {
            const certificates = process.env.REACT_APP_SIGNATURE_CERTIFICATES.split('|');
            setTimeout(async () => {
                await ApryseSDK.verifyAllDigitalSignatures(certificates);
            }, 750);
        }
    };

    const initPdfViewerInstance = () => {
        const element = document.getElementById('viewer');
        if (element) {
            GlobalVariables.licenseKey = lic;
            WebViewer(
                {
                    fullAPI: true,
                    initialDoc:
                        GlobalVariables.isVisualStudioCode && ReduxUpdater.documentURL2 === ''
                            ? ReduxUpdater.documentURL
                            : '', // path to your document
                    licenseKey: lic,
                    path: '/', // point to where the files you copied are served from
                    backendType: 'ems',
                },
                element,
            )
                .then(instance => {
                    const { annotationManager, documentViewer, PDFNet } = instance.Core;

                    if (ReduxUpdater.documentURL2 === '') {
                        loadPdf(instance);
                    }
                    instance.UI.disableElements([
                        'downloadButton',
                        'printButton',
                        'highlightToolGroupButton',
                        'underlineToolGroupButton',
                        'strikeoutToolGroupButton',
                        'squigglyToolGroupButton',
                        'stickyToolGroupButton',
                        'freeTextToolGroupButton',
                        'shapeToolGroupButton',
                        'freeHandToolGroupButton',
                        'freeHandHighlightToolGroupButton',
                        'eraserToolButton',
                        'viewControlsButton',
                        'leftPanelButton',
                        'searchButton',
                        'menuButton',
                        'pageNavOverlay',
                        'header',
                    ]);
                    instance.UI.Fonts.setAnnotationFonts([GlobalConstants.default.fontFamily]);
                    if (customFontURL) {
                        devLog('setCustomFontURL: ', customFontURL);
                        instance.Core.setCustomFontURL(customFontURL);
                    }
                    ApryseSDK.annotationManager = annotationManager;
                    ApryseSDK.documentViewer = documentViewer;
                    ApryseSDK.instance = instance;
                    setViewerReady(true);

                    // Apply dark mode background immediately after instance is ready
                    instance.UI.addEventListener('viewerLoaded', () => {
                        applyViewerBackground(isDark ?? false);
                    });

                    if (ReduxUpdater.documentURL2 !== '') {
                        instance.UI.addEventListener('viewerLoaded', async () => {
                            await ApryseSDK.pdfNet.initialize();

                            const newDoc = await ApryseSDK.generateDocumentDiff(
                                ReduxUpdater.documentURL.endsWith('.pdf')
                                    ? ReduxUpdater.documentURL
                                    : backendApiUrl.pdf.open.replace(
                                        '{documentId}',
                                        ReduxUpdater.documentURL,
                                    ),
                                ReduxUpdater.documentURL2.endsWith('.pdf')
                                    ? ReduxUpdater.documentURL2
                                    : backendApiUrl.pdf.open.replace(
                                        '{documentId}',
                                        ReduxUpdater.documentURL2,
                                    ),
                            );

                            // display the document!
                            // instance is a global variable thats automatically defined inside the config file.
                            instance.UI.loadDocument(newDoc);
                        });
                    }

                    documentViewer.addEventListener('keyDown', onWindowKeyDown);
                    documentViewer.addEventListener('documentLoaded', async () => {
                        devLog('[event] documentLoaded...');
                        applyViewerBackground(isDarkRef.current);
                        ApryseSDK._documentLoaded = true;
                        ApryseSDK._startTime = Date.now();

                        await PDFNet.initialize();
                        ApryseSDK.doc = ApryseSDK.documentViewer.getDocument();
                        ApryseSDK.pdfDoc = await ApryseSDK.documentViewer.getDocument().getPDFDoc();
                        ApryseSDK.undoManager = await ApryseSDK.pdfDoc.getUndoManager();
                        ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.DEFAULT);
                        localStorage.setItem(
                            GlobalConstants.editingSessionTimestamp,
                            new Date().getTime().toString(),
                        );

                        ApryseSDK.adjustAnnotations();
                        await onPdfOpened();
                        await processDigitalSignatures();
                    });
                    documentViewer.addEventListener(
                        'pageComplete',
                        (pageNumber: number, canvas: HTMLCanvasElement) => {
                            ApryseSDK.pageCanvases[pageNumber] = canvas;
                        },
                    );
                    documentViewer.addEventListener('pageNumberUpdated', (pageNumber: number) => {
                        ApryseUpdater.currentPage = pageNumber;
                    });
                    documentViewer.addEventListener(
                        'pageComplete',
                        (pageNumber: number, canvas: HTMLCanvasElement) => {
                            ApryseSDK.pageCanvases[pageNumber] = canvas;
                        },
                    );
                    documentViewer.addEventListener('zoomUpdated', (zoom: number) => {
                        if (isNaN(zoom)) {
                            // Fix for zoom crashing
                            zoom = ApryseUpdater.currentScale * 0.99;
                            GlobalVariables.zoom(zoom);
                        }
                        ApryseUpdater.currentScale = zoom;
                    });
                })
                .catch(err => onPdfError(err));
        }
    };

    const loadPdf = async (instance: WebViewerInstance) => {
        if (GlobalVariables.isVisualStudioCode) {
            return;
        }
        try {
            const url = ReduxUpdater.documentURL.endsWith('.pdf')
                ? backendApiUrl.pdf.openlocal.replace('{documentUrl}', ReduxUpdater.documentURL)
                : backendApiUrl.pdf.open.replace('{documentId}', ReduxUpdater.documentURL);

            const fileResponse = await fetch(url);

            if (fileResponse.status != 200) {
                onPdfError(new Error('PDF could not be loaded.'));

                return;
            }

            const file = await fileResponse.blob();

            if (file.size <= 0) {
                onPdfError(new Error('PDF could not be loaded.'));

                return;
            }

            const extension = await checkExtension(file.slice(0, signatureArrayLength()), () => {
                ReduxUpdater.viewMode = PdfViewerViewModeFlag.Images;
            });
            passwordExtensionRef.current = extension;

            const blobUrl = URL.createObjectURL(file);
            blobUrlRef.current = blobUrl;

            try {
                await instance.Core.documentViewer.loadDocument(blobUrl, { extension });
            } catch (err) {
                const message =
                    typeof err === 'string' ? err : (err as Error)?.message || '';
                if (message.toLowerCase().includes('password')) {
                    devLog('[loadPdf] password required, showing dialog');
                    setPasswordError('');
                    setPasswordDialogOpen(true);
                    return;
                }
                throw err;
            }
        } catch (error) {
            onPdfError(error as Error);
        }
    };

    const cssClasses = React.useMemo(() => {
        if (viewMode === PdfViewerViewModeFlag.Annotation) {
            ApryseSDK.enableApryseUiElements();
        } else {
            ApryseSDK.disableApryseUiElements();
        }

        return [
            styles.viewer,
            'pdf-viewer',
            className,
            `appIsIn${upperCaseBegin(viewMode.toString())}`,
        ];
    }, [viewMode]);

    // Inject/update a <style> inside the WebViewer iframe to control the viewer background
    const applyViewerBackground = React.useCallback((dark: boolean) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframeDoc = (ApryseSDK.instance as any)?.UI?.iframeWindow?.document;
        if (!iframeDoc) return;

        const STYLE_ID = 'eplass-dark-mode-bg';
        const existing = iframeDoc.getElementById(STYLE_ID) as HTMLStyleElement | null;

        if (!dark) {
            // Light mode: remove the injected tag so Apryse uses its own defaults
            existing?.remove();
            return;
        }

        // Dark mode: create the tag if needed, then set dark background
        let styleEl = existing;
        if (!styleEl) {
            const newEl = iframeDoc.createElement('style') as HTMLStyleElement;
            newEl.id = STYLE_ID;
            iframeDoc.head.appendChild(newEl);
            styleEl = newEl;
        }
        styleEl.textContent = `
            /* Apryse viewer background — dark mode, injected by Eplass */
            body,
            #app,
            #app > div,
            .App,
            .document-content-container,
            .DocumentContainer,
            .document-container,
            .ScrollView,
            .scroll-view,
            [data-element="documentContainer"],
            [data-element="documentContent"],
            .documentScrollView,
            .pageContainer {
                background-color: #141414 !important;
            }
        `;
    }, []);

    // Keep ref in sync so the documentLoaded handler always reads the latest value
    React.useEffect(() => {
        isDarkRef.current = isDark ?? false;
    }, [isDark]);

    // Re-apply whenever isDark changes
    React.useEffect(() => {
        applyViewerBackground(isDark ?? false);
    }, [isDark, applyViewerBackground]);

    // Setup/Teardown viewer instance
    React.useEffect(() => {
        ReduxUpdater.viewMode = PdfViewerViewModeFlag.Loading;
        initPdfViewerInstance();

        return () => {
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
            document.removeEventListener('contextmenu', blockContextMenu);
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [documentUrl]);

    return (
        <div
            id={'pdfViewerControl'}
            className={cssClasses.join(' ')}
            ref={elRef}
            style={{
                position:    'relative',
                paddingTop:  showRuler ? 30 : 0,
                paddingLeft: showRuler ? 30 : 0,
                boxSizing:   'border-box',
            }}
        >
            <div id="viewer" className={styles.fullHeight}></div>
            {showRuler && <RulerOverlay viewerReady={viewerReady} containerRef={elRef} />}
            <PasswordDialog
                open={passwordDialogOpen}
                errorMessage={passwordError}
                onConfirm={handlePasswordConfirm}
                onCancel={handlePasswordCancel}
            />
        </div>
    );
};
